/**
 * Phase 8 T-06c §21.E — Smart Match collections picker (localStorage only).
 *
 * Ad-hoc collections the user creates inline from the collections-picker
 * dropdown. Stored under `ggj.smartmatch.collections`. This is UI-only for
 * Phase 8 — the ticket bans server-side persistence — so collections
 * survive refresh within the browser but are **not** shared across devices.
 *
 * `TODO(§8-v2)`: fold into the `requisitions.is_pinned` (or a dedicated
 * `collections` table) when the schema lands.
 */

import type { SmartMatchCollectionRef } from './smartMatchThreads';

const LS_KEY = 'ggj.smartmatch.collections';
export const COLLECTIONS_CHANGE_EVENT = 'ggj:smartmatch-collections-changed';

export interface SmartMatchCollection extends SmartMatchCollectionRef {
  createdAt: number;
  /** Last update time for list ordering (defaults to createdAt for legacy rows). */
  updatedAt: number;
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

type StoredCollection = SmartMatchCollectionRef & { createdAt: number; updatedAt?: number };

function isValid(v: unknown): v is StoredCollection {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    r.id.length > 0 &&
    typeof r.name === 'string' &&
    r.name.length > 0 &&
    typeof r.createdAt === 'number' &&
    (typeof r.updatedAt === 'number' || typeof r.updatedAt === 'undefined')
  );
}

function normalizeCollection(c: StoredCollection): SmartMatchCollection {
  const createdAt = c.createdAt;
  const updatedAt = c.updatedAt ?? createdAt;
  return { ...c, createdAt, updatedAt };
}

export function readCollections(): SmartMatchCollection[] {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isValid)
      .map(normalizeCollection)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

function writeCollections(list: SmartMatchCollection[]): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(LS_KEY, JSON.stringify(list));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(COLLECTIONS_CHANGE_EVENT));
    }
  } catch {
    /* ignore */
  }
}

function newCollectionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createCollection(name: string): SmartMatchCollection {
  const trimmed = name.trim() || 'Unnamed Collection';
  const now = Date.now();
  const collection: SmartMatchCollection = {
    id: newCollectionId(),
    name: trimmed,
    createdAt: now,
    updatedAt: now,
  };
  const list = readCollections();
  writeCollections([collection, ...list]);
  return collection;
}

export function ensureDefaultCollection(): SmartMatchCollection {
  const list = readCollections();
  if (list.length > 0) return list[0];
  return createCollection('Unnamed Collection');
}
